import { useEffect, useState } from "react";
import useDebounce from "../../hooks/useDebounce";
import { useTableHeight } from "../../hooks/useTableHeight";
import { useNavigate } from "react-router";
import { Button, Flex, Form, Grid, Input, message, Modal, Pagination, Select, Table } from "antd";
import { Icon } from "@iconify/react/dist/iconify.js";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { surveyService } from "../../services/survey.service";
import { useAuth } from "../../hooks/useAuth";
import { indonesiaRegionService } from "../../services/indonesia-region.service";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import RenderIf from "../../components/RenderIf";
import DocumentAnalysisResponsiveFilter from "../../components/DocumentAnalysisResponsiveFilter";

export default function MyDocumentsTab() {
  const breakpoint = Grid.useBreakpoint();
  const navigate = useNavigate();
  const { user } = useAuth();
  const tableHeight = useTableHeight();
  const queryClient = useQueryClient();

  const [responsiveFilter, setResponsiveFilter] = useState(false);
  const [modalApi, modalHolder] = Modal.useModal();
  const [messageApi, messageHolder] = message.useMessage();

  const [keyword, setKeyword] = useState("");
  const [marketing, setMarketing] = useState("");
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const debouncedKeyword = useDebounce(keyword, 500);
  const debouncedMarketing = useDebounce(marketing, 500);

  const [marketingQuery, setMarketingQuery] = useLocalStorage('surveyorQuery', debouncedMarketing);

  const [filterForm] = Form.useForm();

  const city = Form.useWatch('city', filterForm);
  const district = Form.useWatch('district', filterForm);
  const village = Form.useWatch('village', filterForm);

  const [params, setParams] = useState({ limit: 10, page: 1, filter: [] });

  useEffect(() => {
    setMarketing(marketingQuery);
  }, [marketingQuery]);

  useEffect(() => {
    setParams(prev => ({
      ...prev,
      page: 1,
      filter: [
        {
          operator: 'orWhere',
          value: [
            { field: 'surveyor', operator: 'like', value: debouncedKeyword },
            { field: 'tracking_id', operator: 'like', value: debouncedKeyword },
            { field: 'nama', operator: 'like', value: debouncedKeyword },
            { field: 'telepon', operator: 'like', value: debouncedKeyword },
            { field: 'rt', operator: 'like', value: debouncedKeyword },
            { field: 'rw', operator: 'like', value: debouncedKeyword },
          ]
        },
        { field: 'marketing', operator: 'like', value: debouncedMarketing },
        { field: 'kota', operator: 'like', value: city },
        { field: 'kecamatan', operator: 'like', value: district },
        { field: 'kelurahan', operator: 'like', value: village },
      ]
    }));
  }, [debouncedKeyword, city, district, village, debouncedMarketing]);

  const { data: surveyDataOfficer, isLoading, isRefetching } = useQuery({
    queryKey: ['lockedData', params.page, params.limit, params.filter],
    queryFn: () => surveyService.getOfficerLockedData(params),
    placeholderData: keepPreviousData,
  });

  const handleChangePage = (page, pageSize) => {
    setParams(p => ({ ...p, page, limit: pageSize }));
  };

  const { data: cities } = useQuery({
    queryKey: ['cities'],
    queryFn: () => indonesiaRegionService.getCities()
  });

  const { data: districts } = useQuery({
    queryKey: ['districts', selectedCity],
    queryFn: () => indonesiaRegionService.getDistricts(selectedCity),
    enabled: !!selectedCity
  });

  const { data: villages } = useQuery({
    queryKey: ['villages', selectedDistrict],
    queryFn: () => indonesiaRegionService.getSubdistricts(selectedDistrict),
    enabled: !!selectedDistrict
  });

  const unlockMutation = useMutation({
    mutationFn: (id) => surveyService.unlockOfficerData(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['registration'] });
      queryClient.invalidateQueries({ queryKey: ['lockedData'] });
      messageApi.success("Berkas berhasil dilepas");
    },
    onError: () => {
      messageApi.error("Terjadi kesalahan");
    }
  });

  useEffect(() => {
    filterForm.resetFields(['district', 'village']);
    if (city) {
      const selected = cities?.find(e => e.name === city);
      setSelectedCity(selected?.id || '');
    }
  }, [city, cities, filterForm]);

  useEffect(() => {
    filterForm.resetFields(['village']);
    if (district) {
      const selected = districts?.find(e => e.name === district);
      setSelectedDistrict(selected?.id || '');
    }
  }, [district, districts, filterForm]);

  const handleChangeMarketingQuery = (q) => {
    setMarketingQuery(q);
    setMarketing(q);
  };

  const columns = [
    {
      title: 'No',
      align: 'center',
      width: 40,
      fixed: breakpoint.md && 'left',
      render: (text, record, index) => index + 1,
    },
    {
      title: 'Tracking ID',
      dataIndex: 'tracking_id',
      width: 150,
      fixed: breakpoint.md && 'left',
    },
    {
      title: 'Nama Pelanggan',
      dataIndex: 'nama',
      width: 180
    },
    {
      title: 'No Telp',
      dataIndex: 'telepon',
      width: 230
    },
    {
      title: 'Kota',
      dataIndex: 'kota',
      width: 280,
    },
    {
      title: 'Kelurahan',
      dataIndex: 'kelurahan',
      width: 150,
    },
    {
      title: 'RT/RW',
      width: 120,
      render: val => `${val.rt?.toString().padStart(3, '0')}/${val.rw?.toString().padStart(3, '0')}`
    },
    {
      title: 'Surveyor',
      dataIndex: 'surveyor',
      width: 150,
    },
    {
      title: 'Marketing',
      dataIndex: 'marketing',
      width: 150,
    },
    {
      title: 'Officer',
      dataIndex: 'officer',
      width: 150,
    },
  ];

  const handleUnlock = (record) => {
    modalApi.confirm({
      centered: true,
      title: 'Pemberitahuan',
      content: `Yakin ingin melepas berkas dengan ID Tracking : ${record?.tracking_id}`,
      onOk: () => unlockMutation.mutate(record.id)
    });
  };

  return (
    <>
      {messageHolder}
      {modalHolder}

      <RenderIf when={breakpoint.lg}>
        <Form form={filterForm}>
          <Flex justify="flex-start" gap={20} style={{ paddingInline: 24, marginBottom: 24 }}>
            <Input
              style={{ width: 250 }}
              prefix={<Icon icon="hugeicons:search-01" width="16" height="16" />}
              placeholder="cari.."
              onChange={e => setKeyword(e.target.value)}
              value={keyword}
              allowClear
            />
            <Input
              prefix={<Icon icon="solar:user-rounded-linear" width="16" height="16" />}
              style={{ width: 220 }}
              placeholder="Marketing"
              onChange={e => handleChangeMarketingQuery(e.target.value)}
              value={marketing}
              allowClear
            />
            <Form.Item noStyle name={'city'}>
              <Select
                allowClear
                options={cities?.map(e => ({ label: e.name, value: e.name }))}
                style={{ width: 200 }}
                placeholder="Kota"
              />
            </Form.Item>
            <Form.Item noStyle name={'district'}>
              <Select
                allowClear
                options={districts?.map(e => ({ label: e.name, value: e.name }))}
                style={{ width: 200 }}
                placeholder="Kecamatan"
                disabled={!city}
              />
            </Form.Item>
            <Form.Item name={'village'} noStyle>
              <Select
                allowClear
                options={villages?.map(e => ({ label: e.name, value: e.name }))}
                style={{ width: 200 }}
                placeholder="Kelurahan"
                disabled={!district}
              />
            </Form.Item>
          </Flex>
        </Form>
      </RenderIf>

      <RenderIf when={!breakpoint.lg}>
        <Flex justify="flex-end" gap={20} style={{ padding: '0 24px', marginBottom: 24 }}>
          <Button
            variant="outlined"
            color="primary"
            icon={<Icon icon="mynaui:filter-solid" width="22" height="22" />}
            onClick={() => setResponsiveFilter(true)}
          >
            Filter
          </Button>
        </Flex>
      </RenderIf>

      <Table
        id="my-registration-table"
        columns={[
          ...columns,
          {
            className: 'last-cell-p',
            title: 'Aksi',
            align: 'right',
            width: 120,
            fixed: 'right',
            render: (record) => (
              <Flex justify="end" gap={10}>
                <Button
                  icon={<Icon icon={record.officer?.length > 0 ? "solar:lock-keyhole-bold-duotone" : "solar:lock-keyhole-unlocked-bold-duotone"} width="18" height="18" />}
                  color="danger"
                  variant="filled"
                  onClick={() => handleUnlock(record)}
                  size=""
                />
                <Button
                  icon={<Icon icon="solar:eye-bold-duotone" width="18" height="18" />}
                  color="primary"
                  variant="filled"
                  onClick={() =>
                    user.user.subdivision === 'Officer'
                      ? navigate('/document-analysis/detail/' + record.id + '/officer')
                      : navigate('/document-analysis/process/detail/' + record.id)
                  }
                  size=""
                />
              </Flex>
            )
          },
        ]}
        size="small"
        loading={isLoading || isRefetching}
        rowKey={'id'}
        scroll={{ y: tableHeight }}
        dataSource={surveyDataOfficer?.data}
        pagination={false}
        rowClassName={() => 'row-open'}
      />

      <Pagination
        style={{ marginInline: 24, marginTop: 16 }}
        total={surveyDataOfficer?.pagination?.total}
        showSizeChanger
        pageSizeOptions={['10', '20', '50']}
        current={surveyDataOfficer?.pagination?.page}
        pageSize={params.limit}
        align="end"
        onChange={handleChangePage}
      />

      <DocumentAnalysisResponsiveFilter
        form={filterForm}
        setKeyword={setKeyword}
        keyword={keyword}
        city={city}
        cities={cities}
        responsiveFilter={responsiveFilter}
        setResponsiveFilter={setResponsiveFilter}
        district={district}
        districts={districts}
        villages={villages}
        handleChangeMarketingQuery={handleChangeMarketingQuery}
        marketing={marketing}
      />
    </>
  );
}